import type { LiteratureDto } from '~/utils/solr/literature'
import { SolrParamsKey } from '~~/shared/solr'
import { createCountWithFilters } from '~/utils/filter-count'
import { extractLabel, extractName, getLastName, processFacets } from '#shared/utils/solr-facet'
import { parseToChronology } from '~/utils/sort'
import { compareLocaleNorwegian, normalizeSearchText } from '~/utils/string'

export interface LiteratureFilterState {
  searchKeyword: string
  showOnlyFavorites: boolean
  typeFilter: { value: string, label: string }[]
  legalAreaFilter: { value: string, label: string }[]
  authorFilter: { value: string, label: string }[]
  sortFilter: { value: string, label: string }
}

type LiteratureDrawerOverrides = Omit<LiteratureFilterState, 'searchKeyword' | 'sortFilter'>

// Keep these comparison keys in sync with `LITERATURE_FACET_CONFIG`.
// Otherwise the sidebar counts can differ from the rendered list.
export function literatureMatches(item: LiteratureDto, state: LiteratureFilterState): boolean {
  if (!matchesAllSearchTerms(state.searchKeyword, [item.title, ...item.authors, ...item.legalAreas])) {
    return false
  }

  if (state.typeFilter.length > 0) {
    const selectedNodeTypes = state.typeFilter.map(f => f.value === 'book' ? 'book' : 'academic_article')
    if (!selectedNodeTypes.includes(item.nodeType)) {
      return false
    }
  }

  if (state.legalAreaFilter.length > 0) {
    const itemAreas = new Set(item.legalAreas.map(area => area.toLowerCase()))
    if (!state.legalAreaFilter.some(f => itemAreas.has(extractLabel(f.value).toLowerCase()))) {
      return false
    }
  }

  if (state.authorFilter.length > 0) {
    const selectedAuthors = new Set(state.authorFilter.map(f => extractName(f.value).toLowerCase()))
    if (!item.authors.some(author => selectedAuthors.has(author.toLowerCase()))) {
      return false
    }
  }

  if (state.showOnlyFavorites && !item.isFavorite) {
    return false
  }
  return true
}

export const LITERATURE_FACET_CONFIG = {
  typeFilter: {
    docKeys: (doc: LiteratureDto) => [doc.nodeType],
    optionKey: (value: string) => value === 'book' ? 'book' : 'academic_article',
  },
  legalAreaFilter: {
    docKeys: (doc: LiteratureDto) => doc.legalAreas.map(area => area.toLowerCase()),
    optionKey: (value: string) => extractLabel(value).toLowerCase(),
  },
  authorFilter: {
    docKeys: (doc: LiteratureDto) => doc.authors.map(author => author.toLowerCase()),
    optionKey: (value: string) => extractName(value).toLowerCase(),
  },
} as const satisfies Partial<Record<FacetKeys<LiteratureFilterState>, FacetIndexConfig<LiteratureDto>>>

const LITERATURE_TYPE_OPTIONS = [
  { value: 'book', label: NODE_TYPE_DISPLAY_LABELS.book },
  { value: 'academic_article', label: NODE_TYPE_DISPLAY_LABELS.academic_article },
]

function _useLiteratureFilters() {
  const searchKeyword = useState<string>('literature-filter-search', () => '')
  const showOnlyFavorites = useState<boolean>('literature-filter-favorites', () => false)
  const typeFilter = useState<{ value: string, label: string }[]>('literature-filter-type', () => [])
  const legalAreaFilter = useState<{ value: string, label: string }[]>('literature-filter-legal-area', () => [])
  const authorFilter = useState<{ value: string, label: string }[]>('literature-filter-author', () => [])
  const sortFilter = useState<{ value: string, label: string }>('literature-filter-sort', () => LITERATURE_SORTING_OPTIONS[0])

  const searchLegalArea = ref<string>('')
  const searchAuthor = ref<string>('')

  const { isFavorite } = useFavorites()

  const { data: solrData, isLoading, suspense } = useSolrQueryV2(
    { [SolrParamsKey.ContentType]: ['book', 'academic_article'] },
    'book',
    'literature',
  )

  const typeOptions = computed(() => {
    const facets = solrData.value?.facet_counts?.facet_fields?.nodeType
    if (!facets) {
      return []
    }

    const available = new Set(facets.filter(item => item.count > 0).map(item => item.value))
    return LITERATURE_TYPE_OPTIONS.filter(option => available.has(option.value))
  })

  const legalAreaOptions = computed(() => {
    const facets = solrData.value?.facet_counts?.facet_fields?.legal_areas
    if (!facets) {
      return []
    }

    const options = processFacets(facets, item => ({
      value: item.value,
      label: extractLabel(item.value),
    }))

    return options.sort((a, b) => compareLocaleNorwegian(a.label, b.label))
  })

  const authorOptions = computed(() => {
    const authorListFacets = solrData.value?.facet_counts?.facet_fields?.author_list || []
    const authorFacets = solrData.value?.facet_counts?.facet_fields?.author || []

    const authorMap = new Map<string, { value: string, count: number }>()

    for (const facet of [...authorListFacets, ...authorFacets]) {
      const name = extractName(facet.value)
      const existing = authorMap.get(name)
      authorMap.set(name, {
        value: facet.value,
        count: (existing?.count || 0) + facet.count,
      })
    }

    const options = processFacets(Array.from(authorMap.values()), item => ({
      value: item.value,
      label: extractName(item.value),
    }))

    // Sort alphabetically by last name
    return options.sort((a, b) => {
      const lastNameA = getLastName(a.label).toLowerCase()
      const lastNameB = getLastName(b.label).toLowerCase()
      return compareLocaleNorwegian(lastNameA, lastNameB)
    })
  })

  const hasFilters = computed(() => {
    return searchKeyword.value.length > 0 || showOnlyFavorites.value || typeFilter.value.length > 0 || legalAreaFilter.value.length > 0 || authorFilter.value.length > 0
  })

  const transformedLiteratureData = computed<LiteratureDto[]>(() => {
    if (!solrData.value?.response?.docs) {
      return []
    }

    return solrData.value.response.docs.filter(item => item !== null).map((item) => {
      const dateOptions = item.nodeType === 'book' ? { year: 'numeric' as const } : undefined
      const lastModified = formatDate(item.lastUpdatedByEditor || item.year, dateOptions)

      const baseItem = {
        id: item.id,
        title: item.title || '[Tittel ikke tilgjengelig]',
        legalAreas: (item.legal_areas || []).map(extractLabel),
        lastModified,
        isFavorite: isFavorite('literature', item.id),
        coverImage: item.coverImage || undefined,
        nodeType: item.nodeType as 'book' | 'academic_article',
        subtitle: item.subtitle || undefined,
        lastUpdatedByEditor: item.lastUpdatedByEditor || undefined,
        dateOfPublication: item.dateOfPublication,
        year: item.year || undefined,
      }

      if (item.nodeType === 'book') {
        return {
          ...baseItem,
          authors: item.author || ['[Forfatterdata ikke tilgjengelig]'],
          edition: item.editionNumber,
          type: 'Fagbok',
        }
      }
      else {
        // academic_article
        return {
          ...baseItem,
          authors: item.authorName || ['[Forfatterdata ikke tilgjengelig]'],
          type: 'Fagartikkel',
          remoteId: item.remoteId || undefined,
        }
      }
    })
  })

  const committedState = computed<LiteratureFilterState>(() => ({
    searchKeyword: searchKeyword.value,
    showOnlyFavorites: showOnlyFavorites.value,
    typeFilter: typeFilter.value,
    legalAreaFilter: legalAreaFilter.value,
    authorFilter: authorFilter.value,
    sortFilter: sortFilter.value,
  }))

  const countForFacet = createCountWithFilters(transformedLiteratureData, committedState, literatureMatches)

  const facetCounts = useFacetIndex(transformedLiteratureData, committedState, literatureMatches, LITERATURE_FACET_CONFIG)

  const { selected: typeSelected, toggle: toggleType, facetOptions: typeFacetOptions } = useFacetFilter(typeFilter, typeOptions, facetCounts.typeFilter)

  const { selected: legalAreaSelected, toggle: toggleLegalArea, facetOptions: legalAreaFacetOptions } = useFacetFilter(legalAreaFilter, legalAreaOptions, facetCounts.legalAreaFilter)

  const { selected: authorSelected, toggle: toggleAuthor, facetOptions: authorFacetOptions } = useFacetFilter(authorFilter, authorOptions, facetCounts.authorFilter)

  const filteredLiterature = computed<LiteratureDto[]>(() => {
    if (!transformedLiteratureData.value) {
      return []
    }
    const state = committedState.value
    return transformedLiteratureData.value.filter(item => literatureMatches(item, state))
  })

  function countWithFilters(overrides: LiteratureDrawerOverrides): number {
    return countForFacet(overrides)
  }

  const totalHits = computed<number>(() => filteredLiterature.value.length)

  const sortedFilteredLiterature = computed<LiteratureDto[]>(() => {
    const literature = filteredLiterature.value

    if (!literature.length) {
      return literature
    }

    const sortBy = sortFilter.value?.value
    const searchTerms = normalizeSearchText(searchKeyword.value).split(/\s+/).filter(Boolean)

    const scores = searchTerms.length > 0
      ? new Map(literature.map(item => [item.id, searchTerms.filter(term =>
          normalizeSearchText(item.title).startsWith(term)
          || item.authors.some(author => normalizeSearchText(author).startsWith(term))
          || item.legalAreas.some(area => normalizeSearchText(area).startsWith(term)),
        ).length]))
      : null

    return [...literature].sort((a, b) => {
      if (scores) {
        const diff = (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0)
        if (diff !== 0) {
          return diff
        }
      }

      if (sortBy === 'kronologisk') {
        const dateA = parseToChronology(a.lastUpdatedByEditor || a.dateOfPublication || a.year)
        const dateB = parseToChronology(b.lastUpdatedByEditor || b.dateOfPublication || b.year)

        return dateB - dateA
      }

      if (sortBy === 'forfatter') {
        const authorA = a.authors[0] || ''
        const authorB = b.authors[0] || ''
        const lastNameA = getLastName(authorA).toLowerCase()
        const lastNameB = getLastName(authorB).toLowerCase()
        return compareLocaleNorwegian(lastNameA, lastNameB)
      }

      const titleA = a.title.toLowerCase()
      const titleB = b.title.toLowerCase()
      return compareLocaleNorwegian(titleA, titleB)
    })
  })

  function toggleFavorite() {
    showOnlyFavorites.value = !showOnlyFavorites.value
  }

  function clearFilters() {
    searchKeyword.value = ''
    showOnlyFavorites.value = false
    typeFilter.value = []
    legalAreaFilter.value = []
    authorFilter.value = []
    searchLegalArea.value = ''
    searchAuthor.value = ''
  }

  return {
    totalHits,
    showOnlyFavorites,
    typeFilter,
    legalAreaFilter,
    authorFilter,
    searchKeyword,
    toggleFavorite,
    clearFilters,
    filteredLiterature,
    sortedFilteredLiterature,
    sortFilter,
    typeOptions,
    legalAreaOptions,
    authorOptions,
    typeFacetOptions,
    legalAreaFacetOptions,
    authorFacetOptions,
    typeSelected,
    legalAreaSelected,
    authorSelected,
    toggleType,
    toggleLegalArea,
    toggleAuthor,
    searchLegalArea,
    searchAuthor,
    isLoading,
    suspense,
    hasFilters,
    countWithFilters,
  }
}

export const useLiteratureFilters = createSharedComposable(_useLiteratureFilters)
